import { useNavigate } from "react-router-dom";
import "./AgendaJobs.scss";
import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import {
    TablePagination,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper
} from "@mui/material";
import { showToast } from "../../../toastService.js";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest.js";
import { ScaleLoader } from "react-spinners";
import { CustomLoadingButton } from "../../../components/ui/CustomButton.jsx";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog.jsx";
import { MdAdd, MdClose, MdSave, MdRefresh } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { LuRefreshCw } from "react-icons/lu";
const formatDateTime = (value) => {
    if (!value) return "-";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleString();
};

const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return "-";
    return `${seconds}s`;
};

const getStatusClass = (status = "") => status.toLowerCase();

const AgendaJobs = () => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);

    const [isLoading, setIsLoading] = useState(false);
    const [jobs, setJobs] = useState([]);
    const [summary, setSummary] = useState({});
    const [failedJobsPreview, setFailedJobsPreview] = useState([]);
    const [runningJobsPreview, setRunningJobsPreview] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [currentJobsPage, setCurrentJobsPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const fetchData = async () => {
        try {
            setIsLoading(true);

            const res = await newRequest.get("/ajobs/dashboard", {
                params: {
                    page: 1,
                    limit: 500,
                    sortBy: "nextRunAt",
                    sortOrder: "desc",
                },
            });

            const payload = res?.data?.data || {};

            setJobs(payload.jobs || []);
            setSummary(payload.summary || {});
            setFailedJobsPreview(payload.failed_jobs_preview || []);
            setRunningJobsPreview(payload.running_jobs_preview || []);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const filteredJobs = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return jobs;

        return jobs.filter((job) => {
            const name = job?.name?.toLowerCase() || "";
            const status = job?.status?.toLowerCase() || "";
            const nextRunAt = job?.nextRunAt ? new Date(job.nextRunAt).toLocaleString().toLowerCase() : "";
            return (
                name.includes(term) ||
                status.includes(term) ||
                nextRunAt.includes(term)
            );
        });
    }, [jobs, searchTerm]);

    const slicedJobs = useMemo(() => {
        return filteredJobs.slice(
            currentJobsPage * rowsPerPage,
            currentJobsPage * rowsPerPage + rowsPerPage
        );
    }, [filteredJobs, currentJobsPage, rowsPerPage]);

    const handleJobsPageChange = (event, newPage) => {
        setCurrentJobsPage(newPage);
    };

    const handleJobsRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setCurrentJobsPage(0);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentJobsPage(0);
    };

    return (
        <div className="agenda-jobs-page">
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <motion.div
                    className="agenda-jobs"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 0.4 } }}
                    exit={{ opacity: 0, transition: { duration: 0.2 } }}
                >
                    <div className="jobs-navbar">
                        <div className="jobs-navbar-left">
                            <h1 className="page-title">{t("jobs").toUpperCase()}</h1>
                        </div>

                        <div className="jobs-navbar-right">
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#0f766e5d"
                                endIcon={<LuRefreshCw />}
                                onClick={fetchData}>
                                {t('refresh')}
                            </CustomLoadingButton>
                        </div>
                    </div>

                    <div className="agenda-jobs-content">
                        <div className="jobs-top-grid">
                            <div className="metric-card metric-card-danger metric-card-wide">
                                <div className="metric-card-label">FAILED JOBS</div>
                                <div className="metric-card-value">{summary.failed || 0}</div>

                                <div className="metric-list">
                                    {failedJobsPreview.length > 0 ? (
                                        failedJobsPreview.map((job) => (
                                            <div className="metric-list-item" key={job._id}>
                                                {job.name}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="metric-list-empty">No failed jobs</div>
                                    )}
                                </div>
                            </div>

                            <div className="metric-card metric-card-info">
                                <div className="metric-card-label">ACTIVE EXECUTION</div>
                                <div className="metric-card-value">{summary.running || 0}</div>

                                <div className="metric-list compact">
                                    {runningJobsPreview.length > 0 ? (
                                        runningJobsPreview.map((job) => (
                                            <div className="metric-list-item" key={job._id}>
                                                {job.name}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="metric-list-empty">No running jobs</div>
                                    )}
                                </div>
                            </div>

                            <div className="metric-card metric-card-success">
                                <div className="metric-card-label">COMPLETED</div>
                                <div className="metric-card-value">{summary.completed || 0}</div>
                            </div>

                            <div className="metric-card">
                                <div className="metric-card-label">TOTAL JOBS</div>
                                <div className="metric-card-value">{summary.total || 0}</div>
                            </div>
                        </div>

                        <div className="jobs-bottom-grid">
                            <div className="jobs-table-card">
                                <div className="jobs-table-header">
                                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px", flex: "2" }}>
                                        <h3 className="sub-title">Problem Queue</h3>
                                        <div className="homeSearch" style={{ height: "40px", width: "60%" }}>
                                            <FiSearch className="homeSearchIcon" />
                                            <input
                                                type="text"
                                                value={searchTerm}
                                                onChange={handleSearchChange}
                                                placeholder={`${t('search')} ${t('jobs')}...`}
                                                className="homeSearchInput"

                                            />
                                            {searchTerm?.length > 0 ? (
                                                <MdClose
                                                    className="homeSearchClear"
                                                    onClick={() => {
                                                        try {
                                                            handleSearchChange();
                                                            setSearchTerm("");
                                                        } catch (error) {
                                                            console.log(error);
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <span className="homeSearchClearPlaceholder" />
                                            )}
                                        </div>
                                    </div>
                                    <span className="jobs-table-count">
                                        {filteredJobs.length} job{filteredJobs.length === 1 ? "" : "s"}
                                    </span>
                                </div>
                                <div className="table-content">
                                    <TableContainer
                                        component={Paper}
                                        sx={{ border: "none", boxShadow: "none", overflow: "visible" }}
                                        className="table-container"
                                    >
                                        <Table sx={{ width: "100%", border: "none" }}>
                                            <TableHead className="tablehead">
                                                <TableRow className="tablerow">
                                                    <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                        #
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ width: "auto", }}>
                                                        <span>{t('job').toUpperCase()}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                        <span>{t('status').toUpperCase()}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "100px" }}>
                                                        <span>{t('duration').toUpperCase()}</span>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                        <span>{t('nextRun').toUpperCase()}</span>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody className="tablebody">
                                                {slicedJobs.length > 0 ? (
                                                    slicedJobs.map((row, index) => (
                                                        <TableRow key={row._id}
                                                            className={`${selectedJob?._id === row?._id ? "tablerow current" : "tablerow"}`}
                                                            onClick={() => setSelectedJob(row)}
                                                        >
                                                            <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                                {currentJobsPage * rowsPerPage + index + 1}
                                                            </TableCell>

                                                            <TableCell className="table-cell left" style={{ width: "auto", }}>
                                                                <div className="job-name">{row.name}</div>
                                                            </TableCell>

                                                            <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                                <span className={`badge ${getStatusClass(row.status)}`}>
                                                                    {row.status}
                                                                </span>
                                                            </TableCell>

                                                            <TableCell className="table-cell left" style={{ maxWidth: "100px" }}>
                                                                {formatDuration(row.duration_seconds)}
                                                            </TableCell>

                                                            <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                                {formatDateTime(row.nextRunAt)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow className="tablerow">
                                                        <TableCell className="table-cell center" colSpan={5}>
                                                            No jobs found
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>

                                        {filteredJobs.length > rowsPerPage && (
                                            <TablePagination
                                                rowsPerPageOptions={[5]}
                                                component="div"
                                                count={filteredJobs.length}
                                                rowsPerPage={rowsPerPage}
                                                page={currentJobsPage}
                                                onPageChange={handleJobsPageChange}
                                                onRowsPerPageChange={handleJobsRowsPerPage}
                                            />
                                        )}
                                    </TableContainer>
                                </div>
                            </div>

                            <div className="side-panel-card">
                                <div className="side-panel-title">Queue Health</div>

                                <div className="side-metric-row">
                                    <div className="side-metric-box">
                                        <span className="side-metric-label"><span>{t('failed').toUpperCase()}</span></span>
                                        <span className="side-metric-value danger">{summary.failed || 0}</span>
                                    </div>

                                    <div className="side-metric-box">
                                        <span className="side-metric-label"><span>{t('running').toUpperCase()}</span></span>
                                        <span className="side-metric-value info">{summary.running || 0}</span>
                                    </div>
                                </div>

                                <div className="side-metric-row">
                                    <div className="side-metric-box">
                                        <span className="side-metric-label"><span>{t('scheduled').toUpperCase()}</span></span>
                                        <span className="side-metric-value">{summary.scheduled || 0}</span>
                                    </div>

                                    <div className="side-metric-box">
                                        <span className="side-metric-label"><span>{t('disabled').toUpperCase()}</span></span>
                                        <span className="side-metric-value">{summary.disabled || 0}</span>
                                    </div>
                                </div>

                                <div className="side-panel-note">
                                    Monitoring Agenda queue status, execution state, scheduling and failures.
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

export default AgendaJobs;